import { createContext, PropsWithChildren, useContext, useMemo } from 'react';

import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';

type MetricSchema = ApiSchemas['MetricsRegistry'];

interface ContextType {
  metricsSchema: MetricSchema[];
  metricsById: Map<string, MetricSchema>;
  isLoading: boolean;
}

const MetricsRegistryContext = createContext<ContextType | null>(null);

export function MetricsRegistryProvider({ children }: PropsWithChildren) {
  const { api } = useApiV1Ctx();
  const { data: metricsSchema = [], isLoading } =
    api.externalCalls.metricsRegistryExternalCallsMetricsRegistryGet.useQuery(undefined, {
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    });

  const metricsById = useMemo(() => {
    const map = new Map<string, MetricSchema>();
    metricsSchema.forEach((metric) => {
      if (metric.key) {
        map.set(metric.key, metric);
      }
    });
    return map;
  }, [metricsSchema]);

  return (
    <MetricsRegistryContext.Provider value={{ metricsSchema, metricsById, isLoading }}>
      {children}
    </MetricsRegistryContext.Provider>
  );
}

export function useMetricsRegistry() {
  const ctx = useContext(MetricsRegistryContext);
  if (!ctx) {
    throw new Error('useMetricsRegistry must be used within a MetricsRegistryProvider');
  }
  return ctx;
}
